import type { Storage } from '@fg/storage';

export interface LifecycleHandlers {
  onPause?: () => void;
  onResume?: () => void;
  onSnapshot?: () => unknown;
  onRestore?: (snapshot: unknown) => void;
  onOrientationChange?: () => void;
  onContextLost?: () => void;
  onContextRestored?: () => void;
}

export interface Lifecycle {
  attach(handlers: LifecycleHandlers): () => void;
  saveSnapshot(): void;
  destroy(): void;
}

export interface LifecycleOptions {
  storage: Storage;
  snapshotKey?: string;
  /** Snapshot poll interval while game is playing. Default 2000ms. */
  intervalMs?: number;
}

export function createLifecycle(opts: LifecycleOptions): Lifecycle {
  const snapshotKey = opts.snapshotKey ?? 'game-snapshot';
  let active: LifecycleHandlers | null = null;
  let intervalHandle: ReturnType<typeof setInterval> | null = null;
  let paused = false;
  let focused = true; // window starts focused
  let destroyed = false;

  function recomputePaused() {
    if (!active) return;
    // We're paused if the document is hidden OR the window has lost focus.
    const shouldBePaused = document.visibilityState === 'hidden' || !focused;
    if (shouldBePaused === paused) return;
    paused = shouldBePaused;
    if (paused) {
      snapshotNow();
      active.onPause?.();
    } else {
      active.onResume?.();
    }
  }

  function onVisibility() {
    recomputePaused();
  }
  function onBlur() {
    focused = false;
    recomputePaused();
  }
  function onFocus() {
    focused = true;
    recomputePaused();
  }
  function onOrientation() {
    active?.onOrientationChange?.();
  }
  function onWebglContextLost(e: Event) {
    e.preventDefault();
    active?.onContextLost?.();
  }
  function onWebglContextRestored() {
    active?.onContextRestored?.();
  }

  function snapshotNow() {
    if (!active?.onSnapshot) return;
    const data = active.onSnapshot();
    void opts.storage.set(snapshotKey, data);
  }

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('blur', onBlur);
  window.addEventListener('focus', onFocus);
  window.addEventListener('orientationchange', onOrientation);
  window.addEventListener('webglcontextlost', onWebglContextLost as EventListener);
  window.addEventListener('webglcontextrestored', onWebglContextRestored as EventListener);

  return {
    attach(handlers) {
      active = handlers;
      // If the window is already in a paused state when this scene attaches,
      // fire onPause once so the handler is in sync with reality.
      if (paused) handlers.onPause?.();
      if (intervalHandle === null && handlers.onSnapshot) {
        intervalHandle = setInterval(snapshotNow, opts.intervalMs ?? 2000);
      }
      if (handlers.onRestore) {
        void opts.storage.get(snapshotKey, null).then((data) => {
          if (data !== null && handlers.onRestore) handlers.onRestore(data);
        });
      }
      return () => {
        active = null;
        if (intervalHandle !== null) {
          clearInterval(intervalHandle);
          intervalHandle = null;
        }
      };
    },

    saveSnapshot() {
      snapshotNow();
    },

    destroy() {
      if (destroyed) return;
      destroyed = true;
      if (intervalHandle !== null) clearInterval(intervalHandle);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('orientationchange', onOrientation);
      window.removeEventListener('webglcontextlost', onWebglContextLost as EventListener);
      window.removeEventListener('webglcontextrestored', onWebglContextRestored as EventListener);
    },
  };
}
